import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
}

const SITE_NAME = "Centre d'expertise familiale de l'Outaouais";
const SITE_URL = "https://www.expertisefamiliale.ca";
const LOGO_URL = `${SITE_URL}/images/logo.png`;

export function SEO({ title, description, keywords, canonical }: SEOProps): null {
  const fullTitle = title === SITE_NAME ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : SITE_URL;

  useEffect(() => {
    // Title
    document.title = fullTitle;

    // Helper to set or create meta tag
    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        const [attrName, attrVal] = selector.replace('meta[', '').replace(']', '').split('="');
        el.setAttribute(attrName, attrVal.replace('"', ''));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    // Standard meta
    setMeta('meta[name="description"]', 'content', description);
    if (keywords) setMeta('meta[name="keywords"]', 'content', keywords);

    // Open Graph
    setMeta('meta[property="og:title"]', 'content', fullTitle);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', canonicalUrl);
    setMeta('meta[property="og:image"]', 'content', LOGO_URL);
    setMeta('meta[property="og:locale"]', 'content', 'fr_CA');
    setMeta('meta[property="og:site_name"]', 'content', SITE_NAME);

    // Twitter Card
    setMeta('meta[name="twitter:card"]', 'content', 'summary');
    setMeta('meta[name="twitter:title"]', 'content', fullTitle);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]', 'content', LOGO_URL);

    // Canonical
    setLink('canonical', canonicalUrl);

    return () => {
      // Reset to default on unmount
      document.title = SITE_NAME;
    };
  }, [fullTitle, description, keywords, canonicalUrl]);

  return null;
}

// Schema.org JSON-LD for LocalBusiness — injected once in index.html
export const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": "https://www.expertisefamiliale.ca",
  "name": "Centre d'expertise familiale de l'Outaouais",
  "alternateName": "CEFO",
  "description": "Services psychosociaux spécialisés en expertise familiale, médiation, coaching coparental et rétablissement du lien parent-enfant en Outaouais.",
  "url": "https://www.expertisefamiliale.ca",
  "logo": "https://www.expertisefamiliale.ca/images/logo.png",
  "image": "https://www.expertisefamiliale.ca/images/logo.png",
  "telephone": "",
  "email": "info@expertisefamiliale.ca",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "39 rue Sainte-Marie",
    "addressLocality": "Gatineau",
    "addressRegion": "QC",
    "postalCode": "J8Y 4A2",
    "addressCountry": "CA"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 45.4419,
    "longitude": -75.7264
  },
  "areaServed": {
    "@type": "AdministrativeArea",
    "name": "Outaouais, Québec"
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "17:00"
    }
  ],
  "priceRange": "$$",
  "currenciesAccepted": "CAD",
  "paymentAccepted": "Cash, Credit Card",
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Services psychosociaux familiaux",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Évaluation psychosociale",
          "description": "Évaluation des capacités parentales et des besoins de l'enfant dans un contexte judiciaire."
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Médiation familiale",
          "description": "Accompagnement neutre pour trouver des ententes concernant la garde et la coparentalité."
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Coaching coparental",
          "description": "Soutien aux parents en situation de coparentalité difficile ou de haut conflit."
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Évaluation psychosociale adoption internationale",
          "description": "Évaluation psychosociale des candidats à l'adoption internationale au Québec."
        }
      }
    ]
  },
  "sameAs": [
    "https://www.expertisefamiliale.ca"
  ]
};
